import React, { useState, useRef, useEffect } from 'react'
import api from '../lib/api'

export default function ShareButton({ vehicleId, shareEnabled, shareToken, premium, onUpgrade, onToggle }) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const ref = useRef(null)

  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : null

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleShare = async () => {
    if (shareEnabled && shareUrl) {
      setOpen(!open)
      return
    }
    setLoading(true)
    try {
      const res = await api.post(`/vehicles/${vehicleId}/toggle_share`)
      onToggle(res.data)
      setOpen(true)
    } catch {
      alert('Failed to enable sharing')
    } finally {
      setLoading(false)
    }
  }

  const handleDisable = async () => {
    setLoading(true)
    try {
      const res = await api.post(`/vehicles/${vehicleId}/toggle_share`)
      onToggle(res.data)
      setOpen(false)
    } catch {
      alert('Failed to disable sharing')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleShare}
        disabled={loading}
        className={`px-3 py-2 text-sm font-semibold border-[1.5px] disabled:opacity-50 ${
          shareEnabled
            ? 'text-accent border-accent hover:bg-white'
            : 'text-ink border-ink hover:bg-white'
        }`}
      >
        <svg className="w-4 h-4 inline-block mr-1 -mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m0-3-3-3m0 0-3 3m3-3v11.25" />
        </svg>
        Share
      </button>

      {open && shareEnabled && shareUrl && (
        <div className="absolute left-0 md:left-auto md:right-0 top-full mt-2 w-[min(20rem,calc(100vw-2rem))] bg-surface border-[1.5px] border-ink shadow-lg p-4 z-10">
          <p className="text-xs text-gray-500 mb-2">Anyone with this link can view the maintenance history</p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 min-w-0 bg-white border border-line px-3 py-2 text-muted truncate"
            />
            <button
              onClick={handleCopy}
              className="px-3 py-2 text-sm font-bold text-white bg-accent hover:bg-accent-dark shrink-0"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {!premium && (
            <p className="mt-2 text-xs text-muted">
              Your share page shows basic info.{' '}
              <button onClick={() => { setOpen(false); onUpgrade() }} className="text-accent hover:text-accent-dark font-medium">
                Upgrade
              </button>
              {' '}to include photos, receipts, and cost analytics.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
